// Phase 25 iter-5e — Farm setup / edit.
//
// O FARMER necesita unha Farm rexistrada antes de pór un animal á venda
// (useGate.canSell.reasons inclúe NEED_FARM mentres farm_id é null). Esta
// páxina crea ou edita esa Farm. Os campos rega_code, sigpac_code, lat,
// lng son opcionais; type+name+municipality son requiridos.

import { Locate, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate } from 'react-router-dom';

import type { FarmResponse } from '@leyrochain/shared';

import { createFarm, getMyFarm, updateFarm } from '../api/farms';
import { describeAxiosError } from '../api/errors';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const FARM_TYPES = [
  { id: 'CARNE', label: 'Cárnica' },
  { id: 'LECHE', label: 'Leiteira' },
  { id: 'MIXTA', label: 'Mixta (cárnica + leiteira)' },
] as const;

const PROVINCES = ['A Coruña', 'Lugo', 'Ourense', 'Pontevedra'];

interface FormState {
  name: string;
  type: 'CARNE' | 'LECHE' | 'MIXTA';
  municipality: string;
  province: string;
  rega_code: string;
  sigpac_code: string;
  lat: string;
  lng: string;
}

const EMPTY: FormState = {
  name: '',
  type: 'CARNE',
  municipality: '',
  province: '',
  rega_code: '',
  sigpac_code: '',
  lat: '',
  lng: '',
};

function farmToForm(f: FarmResponse): FormState {
  return {
    name: f.name,
    type: f.type,
    municipality: f.municipality,
    province: f.province ?? '',
    rega_code: f.rega_code ?? '',
    sigpac_code: f.sigpac_code ?? '',
    lat: f.lat?.toString() ?? '',
    lng: f.lng?.toString() ?? '',
  };
}

export default function FarmFormPage(): JSX.Element {
  const { accessToken, user, refreshUser } = useAuthContext();
  const { addToast } = useToast();
  const [farm, setFarm] = useState<FarmResponse | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    getMyFarm()
      .then((f) => {
        setFarm(f);
        if (f) setForm(farmToForm(f));
      })
      .catch((e) => setError(describeAxiosError(e, 'Non se puido cargar a túa explotación.')))
      .finally(() => setLoading(false));
  }, [accessToken]);

  if (!accessToken) {
    return <Navigate to="/marketplace/login?next=/marketplace/profile/farm" replace />;
  }
  if (user && !user.roles.includes('FARMER')) {
    return (
      <ContentPage
        title="Explotación"
        intro="A xestión de explotación está dispoñible para o rol Gandeiro/a."
        category="Conta"
      >
        <p>Engade o rol dende a páxina de roles e volta aquí.</p>
      </ContentPage>
    );
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleGeolocate() {
    if (!navigator.geolocation) {
      addToast('O teu navegador non soporta xeolocalización.', 'error');
      return;
    }
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        }));
        addToast('Localización capturada.', 'success');
        setGeolocating(false);
      },
      () => {
        addToast('Non se puido obter a túa localización.', 'error');
        setGeolocating(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!form.name.trim() || !form.municipality.trim()) {
      setError('Nome e concello son obrigatorios.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        municipality: form.municipality.trim(),
        ...(form.province.trim() ? { province: form.province.trim() } : {}),
        ...(form.rega_code.trim() ? { rega_code: form.rega_code.trim() } : {}),
        ...(form.sigpac_code.trim() ? { sigpac_code: form.sigpac_code.trim() } : {}),
        ...(form.lat.trim() ? { lat: Number(form.lat) } : {}),
        ...(form.lng.trim() ? { lng: Number(form.lng) } : {}),
      };
      const saved = farm ? await updateFarm(farm.id, payload) : await createFarm(payload);
      setFarm(saved);
      setForm(farmToForm(saved));
      await refreshUser();
      addToast(farm ? 'Explotación actualizada.' : 'Explotación creada.', 'success');
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido gardar a explotación.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ContentPage
      title={farm ? 'Editar a túa explotación' : 'Crear a túa explotación'}
      intro={
        farm
          ? 'Actualiza datos administrativos da túa explotación.'
          : 'Antes de pór animais á venda precisamos rexistrar a explotación. Estes datos compártenser unicamente con compradores cando rexistras unha venda.'
      }
      category="Conta"
    >
      {loading && (
        <div className="flex items-center gap-3 text-sm text-muted">
          <Spinner size={18} /> Cargando…
        </div>
      )}

      {!loading && (
        <form onSubmit={handleSubmit} className="not-prose space-y-5">
          <Field label="Nome da explotación">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              className="airbnb-input"
              required
              placeholder="Ex. Granxa Os Carballos"
            />
          </Field>

          <Field label="Tipo">
            <div className="grid grid-cols-3 gap-2">
              {FARM_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setField('type', t.id)}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    form.type === t.id
                      ? 'border-rauxa bg-rauxa text-white'
                      : 'border-border bg-white text-dark hover:border-dark'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Concello">
              <input
                type="text"
                value={form.municipality}
                onChange={(e) => setField('municipality', e.target.value)}
                className="airbnb-input"
                required
                placeholder="Ex. Lalín"
              />
            </Field>
            <Field label="Provincia (opcional)">
              <select
                value={form.province}
                onChange={(e) => setField('province', e.target.value)}
                className="airbnb-input"
              >
                <option value="">—</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="REGA (opcional)">
              <input
                type="text"
                value={form.rega_code}
                onChange={(e) => setField('rega_code', e.target.value)}
                className="airbnb-input"
                placeholder="ES36..."
              />
            </Field>
            <Field label="SIGPAC (opcional)">
              <input
                type="text"
                value={form.sigpac_code}
                onChange={(e) => setField('sigpac_code', e.target.value)}
                className="airbnb-input"
                placeholder="36:01:02:..."
              />
            </Field>
          </div>

          <Field
            label="GPS (opcional)"
            hint="Úsao para que os compradores vexan onde está a explotación. Non é obrigatorio."
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={form.lat}
                onChange={(e) => setField('lat', e.target.value)}
                className="airbnb-input"
                placeholder="Latitude"
              />
              <input
                type="text"
                value={form.lng}
                onChange={(e) => setField('lng', e.target.value)}
                className="airbnb-input"
                placeholder="Lonxitude"
              />
              <button
                type="button"
                onClick={() => void handleGeolocate()}
                disabled={geolocating}
                className="btn-outline whitespace-nowrap"
              >
                {geolocating ? (
                  <Spinner size={14} />
                ) : (
                  <>
                    <Locate size={14} />
                    Detectar
                  </>
                )}
              </button>
            </div>
          </Field>

          {error && <p className="text-sm text-rauxa">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2"
          >
            {saving ? (
              <Spinner size={16} className="mx-auto" />
            ) : (
              <>
                <Save size={16} />
                {farm ? 'Gardar cambios' : 'Crear explotación'}
              </>
            )}
          </button>
        </form>
      )}
    </ContentPage>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-muted mt-1">{hint}</span>}
    </label>
  );
}
